/**
 * Onboarding Analysis Service
 * 
 * Orchestrates website scraping and AI analysis for customer onboarding.
 * Reuses proven architecture from Google Ads integration.
 */

import { firecrawlService } from './firecrawlService';
import { impressumParser, BusinessInfo } from './impressumParser';
import { geminiService } from './geminiService';
import { openaiService } from './openaiService';
import { getLanguageConfig, type SupportedLanguage } from '../utils/aiLanguageHelper';

// ============================================================================
// Types
// ============================================================================

export interface WebsiteAnalysisResult {
  // Raw Scraped Data
  scraping: {
    url: string;
    scrapedAt: Date;
    success: boolean;
    markdown: string;
    html: string;
    images: string[];
    metadata?: {
      title: string;
      description: string;
    };
  };
  
  // Extracted Business Information
  business: {
    name: string;
    nameConfidence: number;  // 0-100
    industry: 'automotive' | 'insurance' | 'saas' | 'consulting' | 'ecommerce' | 'agency' | 'finance';
    industryConfidence: number;  // 0-100
    industryReasoning: string;
    alternativeIndustries: Array<{ name: string; confidence: number }>;
    location?: {
      country: string;
      city: string;
      address: string;
    };
    contact?: {
      email: string;
      phone: string;
    };
  };
  
  // AI-Powered Analysis
  analysis: {
    suggestedTemplate: string;  // Template ID
    recommendedModules: string[];
    detectedOfferings: string[];
    businessModel?: 'b2b' | 'b2c' | 'b2b2c';
    usp?: string;
    targetAudience?: string;
    brandTone?: 'formal' | 'casual';
    logoUrl?: string;
    logoCandidates?: Array<{
      url: string;
      score: number;
      format: 'svg' | 'png' | 'jpg' | 'webp' | 'unknown';
      location: 'header' | 'nav' | 'footer' | 'body' | 'api';
      altText?: string;
      linksToHome: boolean;
      source?: 'clearbit' | 'google' | 'scraped';
      estimatedSize?: { width: number; height: number };
    }>;
    brandColors?: string[];
    brandFonts?: string[];
    brandImages?: string[]; // NEW: Array of all scraped images
    socialMedia?: {
      linkedin?: string;
      instagram?: string;
      facebook?: string;
      twitter?: string;
    };
    competitors?: string[];
    keywords?: string[];
    slogan?: string;
  };
  
  // Quality Assessment
  quality: {
    overallScore: number;  // 0-100
    hasValidContent: boolean;
    hasBusinessInfo: boolean;
    hasIndustryIndicators: boolean;
    recommendManualReview: boolean;
    issues: string[];
  };
}

// ============================================================================
// Main Analysis Function
// ============================================================================

/**
 * Analyze website for onboarding - orchestrates existing services
 * Pattern: Same as googleAdsService.analyzeLandingPage()
 */
export async function analyzeWebsiteForOnboarding(url: string): Promise<WebsiteAnalysisResult> {
  try {
    console.log('🔍 Starting website analysis for onboarding:', url);
    
    // STEP 1: Scrape with Firecrawl (proven service)
    console.log('🔥 Step 1: Scraping website with Firecrawl...');
    const scrapedData = await firecrawlService.scrapeWebsite(url);
    
    if (!scrapedData || !scrapedData.markdown) {
      throw new Error('Failed to scrape website content');
    }
    
    console.log(`✅ Firecrawl scraped ${scrapedData.markdown.length} chars of content`);
    console.log(`✅ Found ${scrapedData.images.length} images`);
    
    // STEP 2: Extract business info from Impressum (proven service)
    console.log('📍 Step 2: Extracting business info from Impressum...');
    let businessInfo = await impressumParser.findAndExtractBusinessInfo(url);
    
    if (!businessInfo?.city) {
      // Fallback: Try main page footer
      console.log('  ℹ️ Trying main page footer...');
      businessInfo = await impressumParser.extractBusinessInfoFromScrapedContent(
        scrapedData.html,
        scrapedData.markdown,
        url
      );
    }
    
    console.log(`✅ Business info extracted: ${businessInfo?.businessName || 'Unknown'}`);
    
    // STEP 2.5: Extract Logo Candidates (using Firecrawl)
    console.log('🖼️ Step 2.5: Extracting logo candidates...');
    const logoCandidates = await firecrawlService.extractLogoCandidates(url);
    console.log(`✅ Found ${logoCandidates.length} logo candidates`);
    const logoUrl = logoCandidates[0]?.url || null; // Default to top candidate

    // STEP 3: Detect industry with Gemini (NEW function, proven service)
    console.log('🤖 Step 3: Detecting industry with AI...');
    const industryDetection = await geminiService.detectIndustryForOnboarding({
      markdown: scrapedData.markdown,
      businessName: businessInfo?.businessName || '',
      metaDescription: scrapedData.metadata?.description || '',
      images: scrapedData.images
    });
    
    // STEP 3.5: Use Firecrawl's scraped images directly (already have them!)
    console.log('📸 Step 3.5: Processing scraped images...');
    const baseUrl = new URL(url);
    
    // Filter and convert to absolute URLs
    const validBrandImages = scrapedData.images
      .filter(img => {
        const imgLower = img.toLowerCase();
        // Exclude logos (we handle them separately)
        if (imgLower.includes('logo') || imgLower.includes('favicon')) return false;
        // Exclude tiny icons
        if (imgLower.includes('icon') && !imgLower.includes('hero')) return false;
        // Exclude social media icons
        if (imgLower.includes('facebook') || imgLower.includes('twitter') || imgLower.includes('linkedin') || imgLower.includes('instagram')) return false;
        // Must be a valid URL or path
        return img.length > 10;
      })
      .map(img => {
        // Convert relative URLs to absolute
        if (img.startsWith('http://') || img.startsWith('https://')) {
          return img; // Already absolute
        } else if (img.startsWith('//')) {
          return `https:${img}`; // Protocol-relative
        } else if (img.startsWith('/')) {
          return `${baseUrl.protocol}//${baseUrl.host}${img}`; // Absolute path
        } else {
          return `${baseUrl.protocol}//${baseUrl.host}/${img}`; // Relative path
        }
      });
    
    console.log(`✅ Found ${validBrandImages.length} brand images from ${scrapedData.images.length} total images`);

    // STEP 3.6: Extract real colors from HTML/CSS (ALWAYS, not just as fallback)
    console.log('🎨 Step 3.6: Extracting brand colors from HTML/CSS...');
    const extractedColors = await geminiService.extractBrandColors(url, scrapedData.html);
    
    // STEP 3.7: Extract colors from ALL logo candidates (prioritize high-score logos)
    console.log('🎨 Step 3.7: Extracting colors from logo candidates...');
    let logoColors: string[] = [];
    
    // Try top 3 logo candidates (Clearbit, Google Favicon, best scraped)
    const topLogos = logoCandidates.slice(0, 3);
    for (const logo of topLogos) {
      try {
        console.log(`   Extracting from ${logo.source || 'scraped'} logo (score: ${logo.score})...`);
        const colors = await geminiService.extractColorsFromImage(logo.url);
        logoColors.push(...colors);
        console.log(`   ✓ Extracted ${colors.length} colors from ${logo.source || 'scraped'} logo`);
      } catch (error) {
        console.log(`   ℹ️ Could not extract colors from ${logo.source || 'scraped'} logo:`, error);
      }
    }
    
    console.log(`✅ Extracted ${logoColors.length} colors from logo(s)`);
    
    // Combine all color sources and deduplicate
    const allColors = [
      ...logoColors, // Logo colors first (most important)
      ...extractedColors, // HTML/CSS colors
      ...(industryDetection.brandColors || []) // Gemini-detected colors last
    ];
    
    // Deduplicate by normalizing hex codes
    const uniqueColors = Array.from(new Set(
      allColors.map(c => c.toUpperCase().replace(/\s/g, ''))
    ));
    
    // Use combined colors, prioritizing logo colors
    if (uniqueColors.length > 0) {
      industryDetection.brandColors = uniqueColors.slice(0, 10); // Max 10 colors
      console.log(`✅ Final color palette: ${uniqueColors.length} unique colors (logo + HTML + AI)`);
    } else {
      console.log('⚠️ No colors found - will show empty palette');
      industryDetection.brandColors = [];
    }

    console.log(`✅ Industry detected: ${industryDetection.industry} (${industryDetection.confidence}% confidence)`);
    
    // STEP 4: Quality assessment
    const quality = assessScrapingQuality(scrapedData, businessInfo, industryDetection);
    
    console.log(`✅ Quality score: ${quality.overallScore}/100`);
    
    // STEP 5: Build result
    const result: WebsiteAnalysisResult = {
      scraping: {
        url,
        scrapedAt: new Date(),
        success: true,
        markdown: scrapedData.markdown,
        html: scrapedData.html,
        images: scrapedData.images,
        metadata: scrapedData.metadata
      },
      business: {
        name: businessInfo?.businessName || 'Unknown',
        nameConfidence: businessInfo?.businessName ? 95 : 30,
        industry: industryDetection.industry,
        industryConfidence: industryDetection.confidence,
        industryReasoning: industryDetection.reasoning,
        alternativeIndustries: industryDetection.alternatives || [],
        location: businessInfo?.city ? {
          country: businessInfo.country || '',
          city: businessInfo.city,
          address: businessInfo.street || ''
        } : undefined,
        contact: {
          email: businessInfo?.email || '',
          phone: businessInfo?.phone || ''
        }
      },
      analysis: {
        suggestedTemplate: industryDetection.industry,
        recommendedModules: getRecommendedModulesForIndustry(industryDetection.industry),
        detectedOfferings: industryDetection.offerings || [],
        businessModel: industryDetection.businessModel,
        usp: industryDetection.usp,
        slogan: industryDetection.slogan || industryDetection.usp, // Fallback to USP if slogan is missing
        targetAudience: industryDetection.targetAudience,
        brandTone: industryDetection.brandTone,
        logoUrl: logoUrl || undefined,
        logoCandidates: logoCandidates || [], // NEW: All logo candidates for user selection
        brandColors: industryDetection.brandColors || [],
        brandFonts: industryDetection.brandFonts || [],
        brandImages: validBrandImages || [], // Pass images to result
        socialMedia: industryDetection.socialMedia,
        competitors: industryDetection.competitors || [],
        keywords: industryDetection.keywords || []
      },
      quality
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error analyzing website:', error);
    throw new Error(`Failed to analyze website: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Assess quality of scraped data
 */
function assessScrapingQuality(
  scrapedData: any,
  businessInfo: BusinessInfo | null,
  industryDetection: any
): WebsiteAnalysisResult['quality'] {
  const issues: string[] = [];
  let score = 100;
  
  // Check content
  if (!scrapedData.markdown || scrapedData.markdown.length < 500) {
    issues.push('Limited website content found');
    score -= 30;
  }
  
  // Check business name
  if (!businessInfo?.businessName) {
    issues.push('Could not extract company name');
    score -= 25;
  }
  
  // Check industry confidence
  if (industryDetection.confidence < 70) {
    issues.push('Industry detection uncertain');
    score -= 20;
  }
  
  // Check contact info
  if (!businessInfo?.city && !businessInfo?.email) {
    issues.push('Limited contact information');
    score -= 15;
  }
  
  // Check images
  if (scrapedData.images.length === 0) {
    issues.push('No images found');
    score -= 10;
  }
  
  return {
    overallScore: Math.max(0, score),
    hasValidContent: scrapedData.markdown.length >= 500,
    hasBusinessInfo: !!businessInfo?.businessName,
    hasIndustryIndicators: industryDetection.confidence >= 70,
    recommendManualReview: score < 70,
    issues
  };
}

/**
 * Get recommended modules for industry
 * Simple version - will be replaced by moduleService
 */
function getRecommendedModulesForIndustry(industry: string): string[] {
  const moduleMap: Record<string, string[]> = {
    automotive: ['crm_core', 'google_ads'],
    insurance: ['crm_core'],
    saas: ['crm_core', 'google_ads', 'business_planning'],
    consulting: ['crm_core', 'time_tracking', 'business_planning'],
    ecommerce: ['crm_core', 'google_ads', 'business_planning'],
    agency: ['crm_core', 'time_tracking', 'google_ads'],
    finance: ['crm_core']  // Regulated industry - usually no Google Ads
  };
  
  return moduleMap[industry] || ['crm_core'];
}

// ============================================================================
// Deep Research Functions
// ============================================================================

export interface DeepResearchResult {
  marketAnalysis: string;
  competitorInsights: Array<{
    name: string;
    website?: string;
    strengths: string[];
    weaknesses: string[];
    positioning: string;
  }>;
  brandPositioning: string;
  strategicRecommendations: string[];
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Perform deep competitive research using OpenAI o4-mini-deep-research
 * Now supports language parameter for research output in the correct language
 */
export async function performDeepResearch(
  companyName: string,
  websiteUrl: string,
  industry: string,
  scrapedMarkdown: string,
  competitors: string[],
  usp?: string,
  offerings?: string[],
  language?: SupportedLanguage // Language detected from website
): Promise<DeepResearchResult> {
  try {
    // Use provided language or default to German
    const langConfig = getLanguageConfig(language || 'de');
    const isGerman = langConfig.code === 'de';
    
    console.log(`🔍 Starting Deep Research for: ${companyName} (${langConfig.name})`);

    // Language-specific prompt instructions
    const languageInstruction = isGerman
      ? `
🌐 SPRACHE: Alle Antworten MÜSSEN auf DEUTSCH sein!
- Verwende keine englischen Fachbegriffe
- Schreibe alle Analysen, Empfehlungen und Beschreibungen auf Deutsch`
      : `
🌐 LANGUAGE: All responses MUST be in ENGLISH!
- Write all analyses, recommendations, and descriptions in English`;
    
    const researchPrompt = `${languageInstruction}

Perform comprehensive competitive intelligence research for "${companyName}".

COMPANY CONTEXT (from REAL scraped website):
- Website: ${websiteUrl}
- Industry: ${industry}
- USP: ${usp || 'See content below'}
- Offerings: ${offerings?.join(', ') || 'See content below'}

REAL WEBSITE CONTENT (first 10k chars):
${scrapedMarkdown.substring(0, 10000)}

KNOWN COMPETITORS:
${competitors.length > 0 ? competitors.join(', ') : isGerman ? 'Keine erkannt - bitte recherchieren und Wettbewerber finden' : 'None detected - please research and find competitors'}

YOUR RESEARCH TASKS:
1. ${isGerman ? 'Recherchiere jeden Wettbewerber online - finde deren Websites, Wertversprechen und Marktposition' : 'Search for each competitor online - find their websites, value propositions, and market position'}
2. ${isGerman ? `Falls keine Wettbewerber vorhanden, recherchiere und identifiziere 3-5 Hauptwettbewerber in der ${industry}-Branche` : `If no competitors provided, research and identify 3-5 main competitors in the ${industry} industry`}
3. ${isGerman ? `Analysiere die ${industry}-Branche: Marktgröße, Wachstumstrends, Hauptherausforderungen, Chancen` : `Analyze ${industry} industry: market size, growth trends, key challenges, opportunities`}
4. ${isGerman ? `Vergleiche ${companyName} mit Wettbewerbern: Preise, Features, Zielgruppe, Differenzierung` : `Compare ${companyName} against competitors: pricing, features, target audience, differentiation`}
5. ${isGerman ? `Identifiziere die Wettbewerbsvorteile und Lücken von ${companyName} basierend auf dem gescrapten Inhalt` : `Identify ${companyName}'s competitive advantages and gaps based on the scraped content`}
6. ${isGerman ? '5 strategische Empfehlungen zur Verbesserung der Marktposition' : '5 strategic recommendations to improve market position'}

IMPORTANT: 
- ${isGerman ? 'Basiere deine Wettbewerbsanalyse auf ECHTER Online-Recherche' : 'Base your competitor analysis on REAL online research'}
- ${isGerman ? `Nutze den obigen gescrapten Inhalt um das tatsächliche Angebot von ${companyName} zu verstehen` : `Use the scraped content above to understand ${companyName}'s actual offerings`}
- ${isGerman ? 'Keine Halluzinationen - wenn du keine Daten findest, sage es' : 'No hallucinations - if you can\'t find data, say so'}
- ${isGerman ? 'Sei spezifisch und umsetzbar in Empfehlungen' : 'Be specific and actionable in recommendations'}

RETURN structured JSON with this exact format (${isGerman ? 'content in GERMAN' : 'content in ENGLISH'}):
{
  "marketAnalysis": "${isGerman ? '2-3 Sätze über Branchentrends, Marktgröße und Wachstum' : '2-3 sentences about industry trends, market size, and growth'}",
  "competitorInsights": [
    {
      "name": "${isGerman ? 'Wettbewerbername' : 'Competitor Name'}",
      "website": "URL ${isGerman ? 'falls durch Recherche gefunden' : 'if found via research'}",
      "strengths": ["${isGerman ? 'Stärke 1' : 'strength 1'}", "${isGerman ? 'Stärke 2' : 'strength 2'}"],
      "weaknesses": ["${isGerman ? 'Schwäche 1' : 'weakness 1'}", "${isGerman ? 'Schwäche 2' : 'weakness 2'}"],
      "positioning": "${isGerman ? 'Wie sie sich im Markt positionieren' : 'How they position themselves in the market'}"
    }
  ],
  "brandPositioning": "${companyName}${isGerman ? "'s einzigartige Marktposition basierend auf gescraptem Inhalt vs. Wettbewerber" : "'s unique market position based on scraped content vs competitors"}",
  "strategicRecommendations": [
    "${isGerman ? 'Spezifische umsetzbare Empfehlung 1' : 'Specific actionable recommendation 1'}",
    "${isGerman ? 'Spezifische umsetzbare Empfehlung 2' : 'Specific actionable recommendation 2'}",
    "${isGerman ? 'Spezifische umsetzbare Empfehlung 3' : 'Specific actionable recommendation 3'}",
    "${isGerman ? 'Spezifische umsetzbare Empfehlung 4' : 'Specific actionable recommendation 4'}",
    "${isGerman ? 'Spezifische umsetzbare Empfehlung 5' : 'Specific actionable recommendation 5'}"
  ],
  "confidence": "high|medium|low"
}`;

    const response = await openaiService.deepResearch(researchPrompt, {
      max_tokens: 8000
    });
    
    // Parse JSON response
    const cleaned = response.replace(/```json\n?/g, '').replace(/```/g, '').trim();
    const result = JSON.parse(cleaned);
    
    console.log(`✅ Deep Research completed with ${result.confidence} confidence (${langConfig.name})`);
    console.log(`   Found ${result.competitorInsights?.length || 0} competitor insights`);
    console.log(`   Generated ${result.strategicRecommendations?.length || 0} recommendations`);
    
    return result;
    
  } catch (error) {
    console.error('❌ Deep Research failed:', error);
    // Return minimal result in correct language
    const isGerman = (language || 'de') === 'de';
    return {
      marketAnalysis: isGerman 
        ? 'Deep Research derzeit nicht verfügbar.' 
        : 'Deep research unavailable at this time.',
      competitorInsights: [],
      brandPositioning: isGerman ? 'Analyse ausstehend.' : 'Analysis pending.',
      strategicRecommendations: isGerman 
        ? ['Onboarding zuerst abschließen', 'Später manuelle Recherche durchführen']
        : ['Complete onboarding first', 'Run manual research later'],
      confidence: 'low'
    };
  }
}

/**
 * Analyze website with deep research - runs both in parallel
 */
export async function analyzeWebsiteWithDeepResearch(
  url: string,
  progressCallback?: (stage: 'scraping' | 'logoExtraction' | 'colorExtraction' | 'deepResearch', status: 'running' | 'complete' | 'error') => void
): Promise<{
  analysis: WebsiteAnalysisResult;
  deepResearch: DeepResearchResult;
}> {
  try {
    console.log('🚀 Starting comprehensive analysis with Deep Research...');
    
    // STEP 1: Run standard analysis (includes scraping, logo, colors)
    progressCallback?.('scraping', 'running');
    const analysis = await analyzeWebsiteForOnboarding(url);
    progressCallback?.('scraping', 'complete');
    progressCallback?.('logoExtraction', 'complete');
    progressCallback?.('colorExtraction', 'complete');
    
    // STEP 2: Start deep research immediately (don't wait)
    // Deep research runs while user watches video - saves ~30 seconds!
    progressCallback?.('deepResearch', 'running');
    
    // Detect language from scraped metadata for research output
    const detectedLanguage = (analysis.scraping.metadata?.language?.substring(0, 2) || 'de') as SupportedLanguage;
    console.log(`🌐 Detected website language: ${detectedLanguage}`);
    
    let deepResearch: DeepResearchResult;
    try {
      deepResearch = await performDeepResearch(
        analysis.business.name,
        url,
        analysis.business.industry,
        analysis.scraping.markdown,
        analysis.analysis.competitors || [],
        analysis.analysis.usp,
        analysis.analysis.detectedOfferings,
        detectedLanguage // Pass detected language for research output
      );
      progressCallback?.('deepResearch', 'complete');
      console.log(`✅ Deep Research completed successfully (${detectedLanguage})`);
    } catch (researchError) {
      // Don't fail entire onboarding if deep research fails
      console.warn('⚠️ Deep Research failed, using fallback:', researchError);
      progressCallback?.('deepResearch', 'complete'); // Mark as complete even if failed
      // Return fallback in detected language
      const isGerman = detectedLanguage === 'de';
      deepResearch = {
        marketAnalysis: isGerman 
          ? 'Deep Research vorübergehend nicht verfügbar.' 
          : 'Deep research temporarily unavailable.',
        competitorInsights: [],
        brandPositioning: isGerman 
          ? 'Die Analyse wird in Ihrem CRM-Dashboard verfügbar sein.' 
          : 'Analysis will be available in your CRM dashboard.',
        strategicRecommendations: isGerman 
          ? ['Onboarding zuerst abschließen', 'Deep Research später im Firmenprofil aufrufen']
          : ['Complete onboarding first', 'Access deep research in Company Profile later'],
        confidence: 'low'
      };
    }
    
    console.log('✅ Comprehensive analysis with Deep Research completed');
    
    return { analysis, deepResearch };
    
  } catch (error) {
    progressCallback?.('scraping', 'error');
    progressCallback?.('deepResearch', 'error');
    console.error('❌ Error in comprehensive analysis:', error);
    throw error;
  }
}

// ============================================================================
// Export
// ============================================================================

export const onboardingAnalysisService = {
  analyzeWebsiteForOnboarding,
  performDeepResearch,
  analyzeWebsiteWithDeepResearch
};

